import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Box,
  CircularProgress,
  InputAdornment,
  IconButton,
  Autocomplete,
  Divider,
  Paper,
  Stack,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Person,
  Business,
  Lock,
  Clear,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../../context/AuthContext';
import { useInfoscreens } from '../../../../infoscreens/hooks/useInfoscreens';
import { scannerLogin } from '../../../../../services/scannerSession';
import { useNavigationStack } from '../../../../../context/NavigationStackContext';
import { Link, useNavigate } from 'react-router-dom';

function toScannerCredentials({ department_name, staff_member_name, password }) {
  return { department_name, name: staff_member_name, password };
}

export default function LoginCard({ onDepartmentChange }) {
  const { t } = useTranslation(['auth', 'common']);
  const { login, isLoading, error: authError, clearError } = useAuth();
  const [scannerError, setScannerError] = useState(null);
  const error = scannerError || authError;
  const { checkInfoscreen } = useInfoscreens();
  const { clearStack } = useNavigationStack();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    department_name: '',
    staff_member_name: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const [savedDepartments, setSavedDepartments] = useState([]);

  // Load saved departments from localStorage on component mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem('orbit_departments');
      const lastLogin = localStorage.getItem('orbit_last_login_department');

      if (saved) {
        setSavedDepartments(JSON.parse(saved));
      }

      // Prioritize the last successful login department for auto-fill
      if (lastLogin) {
        setFormData(prev => ({ ...prev, department_name: lastLogin }));
      } else if (saved) {
        const departments = JSON.parse(saved);
        if (departments.length > 0) {
          setFormData(prev => ({ ...prev, department_name: departments[0] }));
        }
      }
    } catch (error) {
      console.error('Error loading departments from localStorage:', error);
      // If there's an error, clear the corrupted data
      localStorage.removeItem('orbit_departments');
      localStorage.removeItem('orbit_last_login_department');
    }
  }, []);

  // Save departments to localStorage whenever the list changes
  const saveDepartmentsToStorage = (departments) => {
    try {
      localStorage.setItem('orbit_departments', JSON.stringify(departments));
      setSavedDepartments(departments);
    } catch (error) {
      console.error('Error saving departments to localStorage:', error);
      // Still update state even if localStorage fails
      setSavedDepartments(departments);
    }
  };

  const addDepartmentToHistory = (departmentName) => {
    if (!departmentName.trim()) return;

    const trimmed = departmentName.trim();
    if (!savedDepartments.includes(trimmed)) {
      const newDepartments = [trimmed, ...savedDepartments];
      saveDepartmentsToStorage(newDepartments);
    }
  };

  const removeDepartmentFromHistory = (departmentToRemove) => {
    const filtered = savedDepartments.filter(dept => dept !== departmentToRemove);
    saveDepartmentsToStorage(filtered);
  };

  const handleInputChange = (field) => (event, value) => {
    // Handle both regular input events and Autocomplete value changes
    const newValue = value !== undefined ? value : event.target.value;

    setFormData(prev => ({ ...prev, [field]: newValue }));

    if (field === 'department_name') {
      onDepartmentChange?.(newValue ?? '');
    }

    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Clear auth error when user modifies form
    if (scannerError) setScannerError(null);
    if (authError) {
      clearError();
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.department_name.trim()) {
      errors.department_name = t('auth:validation.department_name_required');
    }

    if (!formData.staff_member_name.trim()) {
      errors.staff_member_name = t('auth:validation.staff_member_name_required');
    }

    if (!formData.password) {
      errors.password = t('auth:validation.password_required');
    } else if (formData.password.length < 8) {
      errors.password = t('auth:validation.password_min_length');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    // First, try to check if it's an infoscreen login
    const infoscreenResult = await checkInfoscreen(
      formData.department_name,
      formData.staff_member_name,
      formData.password
    );

    if (infoscreenResult.success) {
      // Store infoscreen data to localStorage
      try {
        localStorage.setItem('orbit_perm_infoscreen', JSON.stringify({
          ...infoscreenResult.data,
          department_name: formData.department_name,
        }));
        clearStack();
        navigate('/infoscreens/show');
        return;
      } catch (error) {
        console.error('Error saving infoscreen data:', error);
      }
    }

    // Then a scanner phone. 404 means no scanner has this name → staff login
    try {
      await scannerLogin(toScannerCredentials(formData));
      clearStack();
      navigate('/scanner');
      return;
    } catch (scannerErr) {
      if (scannerErr.response?.status !== 404) {
        const code = scannerErr.response?.data?.error_code;
        setScannerError(code
          ? t(`errors:${code}`, { defaultValue: scannerErr.response.data.message })
          : t('errors:NETWORK'));
        return;
      }
    }

    // If infoscreen and scanner checks failed, try regular staff login
    const result = await login(formData);

    if (result.success) {
      clearStack();
      // Save department to history and remember as last successful login
      addDepartmentToHistory(formData.department_name);

      try {
        localStorage.setItem('orbit_last_login_department', formData.department_name);
      } catch (error) {
        console.error('Error saving last login department:', error);
      }
    } else {
      // Error is handled by the auth context
      console.error('Login failed:', result.error);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Card
      elevation={8}
      sx={{
        maxWidth: 440,
        width: '100%',
        position: 'relative',
        borderRadius: 3,
        overflow: 'hidden',
        opacity: 0.85,
        boxShadow: 7
      }}
    >
      {/* Medical Header Section */}
      <Paper
        elevation={0}
        sx={{
          px: 3,
          pt: 8,
          pb: 0,
          textAlign: 'center',
          position: 'relative'
        }}
      >
        <Box component="img"
          src="/images/logos/orbit-by-dentec-on-white.svg"
          alt={t("common:appname")}
          sx={{ width: 240, mt: 1, mb: 1 }}
        />
        {/* <Typography variant="body1" sx={{ opacity: 0.9, fontWeight: 300 }}>
          {t('auth:sign_in_subtitle')}
        </Typography> */}
      </Paper>

      <CardContent sx={{ p: 4 }}>
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: 2,
              '& .MuiAlert-message': {
                fontWeight: 500
              }
            }}
          >
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Autocomplete
            freeSolo
            disableClearable
            options={savedDepartments}
            value={formData.department_name}
            onChange={handleInputChange('department_name')}
            onInputChange={handleInputChange('department_name')}
            disabled={isLoading}
            ListboxProps={{
              style: { maxHeight: 200 }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                label={t('auth:department_name')}
                variant="outlined"
                margin="normal"
                error={!!validationErrors.department_name}
                helperText={validationErrors.department_name}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2
                  }
                }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Business color="primary" />
                    </InputAdornment>
                  ),
                }}
              />
            )}
            renderOption={({ key, ...props }, option) => (
              <Box key={key}
                {...props}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between !important',
                  alignItems: 'center',
                  py: 1.5,
                  '&:hover .delete-btn': {
                    opacity: 1
                  }
                }}
              >
                <Box>
                  <Stack>
                    <Typography fontWeight="500">{option}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t("auth:recent_practice")}
                    </Typography>
                  </Stack>
                </Box>

                <IconButton
                  className="delete-btn"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeDepartmentFromHistory(option);
                  }}
                  sx={{
                    opacity: 0,
                    transition: 'opacity 0.2s',
                    ml: 1,
                    color: 'text.secondary',
                    '&:hover': {
                      color: 'error.main',
                      backgroundColor: 'rgba(244, 67, 54, 0.04)'
                    }
                  }}
                  title={t('common:buttons.delete')}
                >
                  <Clear fontSize="small" />
                </IconButton>
              </Box>
            )}
          />

          <TextField
            fullWidth
            label={t('auth:staff_member_name')}
            variant="outlined"
            margin="normal"
            name='username'
            value={formData.staff_member_name}
            onChange={handleInputChange('staff_member_name')}
            error={!!validationErrors.staff_member_name}
            helperText={validationErrors.staff_member_name}
            disabled={isLoading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Person color="primary" />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label={t('auth:password')}
            type={showPassword ? 'text' : 'password'}
            variant="outlined"
            margin="normal"
            value={formData.password}
            onChange={handleInputChange('password')}
            error={!!validationErrors.password}
            helperText={validationErrors.password}
            disabled={isLoading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock color="primary" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={togglePasswordVisibility}
                    edge="end"
                    disabled={isLoading}
                    title={showPassword ? t('auth:hide_password') : t('auth:show_password')}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.04)'
                      }
                    }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={isLoading}
            sx={{
              mt: 4,
              mb: 3,
              py: 1.8,
              borderRadius: 2,
              fontWeight: 600,
              fontSize: '1.1rem',
              background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
              }
            }}
          >
            {isLoading ? (
              <Box display="flex" alignItems="center" gap={2}>
                <CircularProgress size={20} color="inherit" />
                <Typography variant="button">
                  {t('auth:signing_in')}
                </Typography>
              </Box>
            ) : (
              t('auth:sign_in')
            )}
          </Button>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box textAlign="center">
          <Typography variant="caption" color="text.secondary">
            {t('auth:contact_admin_message')}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
            {t("auth:privacy_policy.accept")}{' '}
          <Link 
            to="/privacy-policy" 
            style={{ color: 'inherit', textDecoration: 'underline' }}
          >
            {t("auth:privacy_policy.name")}
          </Link>
        </Typography>
      </CardContent>
    </Card>
  );
}